import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { ConditionRow } from './ConditionRow';
import { daysPastEta, getEscalation, sortConditions } from '../../utils/conditions';
import type { ConditionPatch } from '../../hooks/useConditions';
import type { Condition, SortState } from '../../types/conditions';
interface ConditionSectionProps {
  title: string;
  conditions: Condition[];
  emptyMessage: string;
  sort: SortState | null;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onUpdate: (id: string, patch: ConditionPatch) => void;
  onComments: (id: string) => void;
  onUpload: (id: string) => void;
  onLinkDocuments: (id: string) => void;
  defaultOpen?: boolean;
  /** Show which group (Processor / Admin / Title) each row belongs to. */
  showCategory?: boolean;
}
export function ConditionSection({
  title,
  conditions,
  emptyMessage,
  sort,
  selectedIds,
  onToggleSelect,
  onUpdate,
  onComments,
  onUpload,
  onLinkDocuments,
  defaultOpen = true,
  showCategory = false
}: ConditionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const sorted = sortConditions(conditions, sort);
  const needsAttention = conditions.filter(c => daysPastEta(c) > 0 || getEscalation(c) !== 'none').length;
  const panelId = `group-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return <section aria-label={`${title} conditions`} data-id="element-114" data-mp="1tpjyi7ons4j6">
      <h3 data-id="element-115" data-mp="1to8wacav34u6">
        <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls={panelId} className="flex w-full items-center gap-3 border-y border-slate-300 bg-slate-200/70 px-6 py-3 text-left transition-colors hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand" data-id="element-116" data-mp="1pp9rcrp4r82q">
          <motion.span animate={{
          rotate: open ? 0 : -90
        }} transition={{
          duration: 0.15
        }} className="flex" data-id="element-117" data-mp="28friwsy87xmi">
            <ChevronDownIcon className="h-4 w-4 text-charcoal" aria-hidden="true" data-id="element-118" data-mp="2fj7q51yp2eyz" />
          </motion.span>
          <span className="text-[15px] font-semibold text-charcoal" data-id="element-119" data-mp="2kh0yo1ngzl2f">{title}</span>
          <span className="min-w-[22px] rounded-full bg-white px-1.5 py-0.5 text-center text-xs font-medium tabular-nums text-slate-600 ring-1 ring-inset ring-slate-300" data-id="element-120" data-mp="2ph5c1it2zzof">
            {conditions.length}
          </span>
          {needsAttention > 0 && <span className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600" data-id="element-121" data-mp="2z6f9le6p2ajx">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden="true" data-id="element-122" data-mp="387dahnlu61ya" />
              {needsAttention} need{needsAttention === 1 ? 's' : ''} attention
            </span>}
        </button>
      </h3>

      <AnimatePresence initial={false} data-id="element-123" data-mp="1du5ehsm4jkb2">
        {open && <motion.div id={panelId} role="rowgroup" initial={{
        height: 0,
        opacity: 0,
        overflow: 'hidden'
      }} animate={{
        height: 'auto',
        opacity: 1,
        transitionEnd: {
          overflow: 'visible'
        }
      }} exit={{
        height: 0,
        opacity: 0,
        overflow: 'hidden'
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} data-id="element-124" data-mp="1dvovxmjio1ef">
            {conditions.length === 0 ? <p className="border-b border-slate-100 px-6 py-4 pl-[60px] text-sm text-slate-500" data-id="element-125" data-mp="wk9wip4bjvct">{emptyMessage}</p> : <AnimatePresence initial={false} data-id="element-126" data-mp="1u6qmtaemx8p2">
                {sorted.map(c => <ConditionRow key={c.id} condition={c} selected={selectedIds.has(c.id)} onToggleSelect={() => onToggleSelect(c.id)} onUpdate={patch => onUpdate(c.id, patch)} onComments={() => onComments(c.id)} onUpload={() => onUpload(c.id)} onLinkDocuments={() => onLinkDocuments(c.id)} showCategory={showCategory} data-id="element-127" data-mp="1p8fdd0s9507d" />)}
              </AnimatePresence>}
          </motion.div>}
      </AnimatePresence>
    </section>;
}